import { createSelector } from 'reselect'
import { getCurrentAccountSelector } from './index'
import {
  getVisibleUsersWithTeamsForCompanyOwner,
  getVisibleUsersWithTeamsForDealOwner,
  getVisibleUsersWithTeamsForPersonOwner
} from 'VisibleUsers/selectors'
import { filterActiveUsers } from 'modules/userFiltering'
import orderBy from 'modules/orderBy'

export const rewindUserV3ToOrig = (user) => ({ ...user, name: user.full_name, active: user.deleted_at === null })

export const getAccountId = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.id
)

export const getFutureChangesSelector = state => state.modern.currentAccount.future_changes

export const getFutureDowngrades = createSelector(
  getFutureChangesSelector,
  futureChanges => futureChanges.filter(futureChange => futureChange.change_type === 'downgrade')
)

export const getFutureLicenseDowngrades = createSelector(
  getFutureDowngrades,
  futureDowngrades => futureDowngrades.filter(futureDowngrade => futureDowngrade.change_options &&
    futureDowngrade.change_options.license_count)
)

export const getDealPipelines = state => state.modern.currentAccount.deal_pipelines
export const getDefaultDealPipeline = createSelector(
  getDealPipelines,
  pipelines => pipelines.find(pipeline => pipeline.default)
)
export const getDealRevenueTypes = state => state.modern.currentAccount.revenue_types
export const getDealLossReasons = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_loss_reasons
)

export const getDealLossReasonRequired = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_loss_reason_required
)

export const getDealStatuses = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_statuses
)

export const getShowDealLossReasonDialog = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.show_deal_loss_reason_dialog
)

export const getShowDealLossReasonDealProfile = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.show_deal_loss_reason_deal_profile
)

export const getDealWonReasons = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_won_reasons
)
export const getDealWonReasonRequired = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_won_reason_required
)
export const getShowDealWonReasonDialog = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.show_deal_won_reason_dialog
)

export const getShowDealWonReasonDealProfile = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.show_deal_won_reason_deal_profile
)

export const getPersonStatuses = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.lead_statuses
)

export const getPersonTags = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.predefined_contacts_tags
)

export const getCompanyTags = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.company_tags
)

export const getDealTags = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_tags
)

export const getDealStages = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_stages
)

export const getLeadSources = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.lead_sources
)

export const getEmailSequenceRecipientStatuses = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.email_sequence_recipient_statuses
)

export const getActivityCategories = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.note_categories
)

export const getActivityCategoriesWithSystemCategories = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.note_categories_with_system_categories
)

export const getNotEditableNoteSystemCategories = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.note_categories_with_system_categories.filter(category => !category.editable)
)

export const getAccountExports = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.account_exports
)

export const getEventCategories = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.event_categories
)

export const getTodoTemplates = state => state.modern.currentAccount.todo_templates
export const getProjectTemplates = state => state.modern.currentAccount.project_templates || []
export const getProductLines = state => state.modern.currentAccount.product_lines
export const getAccountPlan = state => state.modern.currentAccount.plan

export const shouldShowDealLossReasonDialog = state => !!state.modern.currentAccount.show_deal_loss_reason_dialog
export const shouldShowDealWonReasonDialog = state => !!state.modern.currentAccount.show_deal_won_reason_dialog
export const shouldShowDealLossReasonDealProfile = state => !!state.modern.currentAccount.show_deal_loss_reason_deal_profile
export const shouldShowDealWonReasonDealProfile = state => !!state.modern.currentAccount.show_deal_won_reason_deal_profile

export const isQuickbooksEnabled = state => state.modern.currentAccount.quickbooks_enabled

export const getDocumentTags = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.document_tags
)

export const getUsers = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.users
)

export const getActiveUsers = createSelector(
  getUsers,
  users => users.filter(user => user.deleted_at === null)
)

export const getInactiveUsers = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.inactive_users
)

export const getTeams = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.teams
)

export const getVisibleActiveUsersForCompanyOwner = createSelector(
  getVisibleUsersWithTeamsForCompanyOwner,
  visibleUsers => {
    const visibleUsersWithoutTeams = visibleUsers.filter(user => !user.id.toString().endsWith('_team'))
    return filterActiveUsers(visibleUsersWithoutTeams)
  }
)

export const getVisibleActiveUsersForDealOwner = createSelector(
  getVisibleUsersWithTeamsForDealOwner,
  visibleUsers => {
    const visibleUsersWithoutTeams = visibleUsers.filter(user => !user.id.toString().endsWith('_team'))
    return filterActiveUsers(visibleUsersWithoutTeams)
  }
)

export const getVisibleActiveUsersForPersonOwner = createSelector(
  getVisibleUsersWithTeamsForPersonOwner,
  visibleUsers => {
    const visibleUsersWithoutTeams = visibleUsers.filter(user => !user.id.toString().endsWith('_team'))
    return filterActiveUsers(visibleUsersWithoutTeams)
  }
)

export const getVisibleUsers = createSelector(
  getUsers,
  getInactiveUsers,
  (users, usersInactive) => ([
    ...orderBy(users.map(user => rewindUserV3ToOrig(user)), 'full_name'),
    ...orderBy(usersInactive.map(user => rewindUserV3ToOrig(user)), 'full_name')
  ])
)

export const getUsersLevelNumber = (state, props) => props.level
export const getUsersLevel = createSelector(
  getCurrentAccountSelector,
  getUsersLevelNumber,
  (currentAccount, level) => currentAccount.users.filter(user => user.level === level)
)

export const getExternalUsers = state => getUsersLevel(state, { level: 5 })

export const getReadOnlyUsers = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.users.filter(user => user.read_only)
)

export const getNonReadOnlyUsers = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.users.filter(user => !user.read_only)
)

export const getAdminUsers = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.users.filter(user => user.is_account_admin)
)

export const getHigherUpUsers = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.higher_up_users
)

export const getAccountDealStages = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_stages
)

export const getPipelineId = (state, props) => props.dealPipelineId

export const getCurrentDealStages = createSelector(
  getAccountDealStages,
  getPipelineId,
  (stages, dealPipelineId) => {
    return !dealPipelineId ? [] : stages.filter(stage => stage.deal_pipeline_id === dealPipelineId)
  }
)

export const getCalendarEntryPriorities = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_priorities
)

export const getCalendarEntryStatuses = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_statuses
)

export const getProjectMilestoneStatuses = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.project_milestone_statuses || []
)

export const getForms = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.forms
)

const getFields = (field) => {
  if (!field.hasOwnProperty('children')) return field

  return field.children.map(i => getFields(i)).flat()
}

export const getCompanyForm = createSelector(
  getForms,
  forms => forms.company
)

export const getCompanyQuickAddForm = createSelector(
  getForms,
  forms => forms.companyQuickAdd
)

export const getCompanyFormFields = createSelector(
  getCompanyForm,
  form => form.map(g => getFields(g)).flat()
)

export const getDealForm = createSelector(
  getForms,
  forms => forms.deal
)

export const getDealQuickAddForm = createSelector(
  getForms,
  forms => forms.dealQuickAdd
)

export const getDealFormFields = createSelector(
  getDealForm,
  form => form.map(g => getFields(g)).flat()
)

export const getPersonForm = createSelector(
  getForms,
  forms => forms.person
)

export const getPersonQuickAddForm = createSelector(
  getForms,
  forms => forms.personQuickAdd
)

export const getPersonFormFields = createSelector(
  getPersonForm,
  form => form.map(g => getFields(g)).flat()
)

export const getFormFieldId = (state, props) => props.formFieldId
export const getCompanyFormField = createSelector(
  getCompanyForm,
  getFormFieldId,
  (form, formFieldId) => {
    return form.map(s => s.children).flat().find(field => field.id === formFieldId)
  }
)

export const getCalendarEntryRecurringEndDateMonths = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_recurring_end_date_months
)

export const getAccountLanguage = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.language || 'en'
)

export const getCalendarEntryPostponePeriods = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_postpone_periods.map(period => parseInt(period))
)

export const getCalendarEntryPostponeWeekends = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_postpone_weekends
)

export const getCalendarEntryTaskDueDateRequired = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_task_due_date_required
)

export const getCalendarEntryUseCalendarToPostpone = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_use_calendar_to_postpone
)

export const getTodoTemplatesIgnoreWeekends = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.todo_templates_ignore_weekends
)

export const getDealCustomFieldLabels = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_custom_field_labels
)

export const getDealCalculatedCustomFieldLabels = createSelector(
  getDealCustomFieldLabels,
  dealCustomFieldLabels => (dealCustomFieldLabels || []).filter(field => field.field_type === 'calculated')
)

export const getPersonCustomFieldLabels = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.person_custom_field_labels
)

export const getCompanyCustomFieldLabels = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.company_custom_field_labels
)

export const getCustomFieldGroups = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.custom_field_groups
)

export const getDealCustomFieldGroups = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.custom_field_groups.filter(item => item.type === 'DealCustomFieldGroup')
)

export const getPersonCustomFieldGroups = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.custom_field_groups.filter(item => item.type === 'PersonCustomFieldGroup')
)

export const getCompanyCustomFieldGroups = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.custom_field_groups.filter(item => item.type === 'CompanyCustomFieldGroup')
)

export const getProjectCustomFieldLabels = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.project_custom_field_labels || []
)

export const getProjectCustomFieldGroups = createSelector(
  getCurrentAccountSelector,
  currentAccount => (currentAccount.custom_field_groups || []).filter(item => item.type === 'ProjectCustomFieldGroup')
)

export const getCalculatedFieldsAttributes = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calculated_fields_attributes
)

export const getCurrencies = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.currencies
)

export const getUpgradeToGrowUrl = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.upgrade_to_grow
)

export const getUpgradeToDevelopUrl = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.upgrade_to_develop
)

export const getConditionalFields = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.conditional_fields
)

export const getConditionalFieldsDefaults = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.conditional_fields_defaults
)

export const getMergeTags = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.merge_tags
)

export const getMfaEnforced = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.mfa_enforced
)

export const getCompanyName = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.company_name
)

export const getIsTrialConvertedEarly = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.is_trial_converted_early
)

export const getIsTestAccount = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.is_test_account
)

export const getIsApiEnabled = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.is_api_enabled
)

export const getCurrentBrandSelector = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.brand || {
    header_color: '#ffffff',
    logo_choice: 'pipelinedeals_logo'
  }
)

export const getAllowUsersToConfigureEditMode = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.allow_users_to_configure_edit_mode
)

export const getAccountEditModeEnabledByDefault = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.edit_mode_enabled_by_default
)

export const getAllowUsersToConfigureProfileDetailsFields = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.allow_users_to_configure_profile_details_fields
)

export const getTrainingVideosBannerAllowDismiss = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.training_videos_banner_allow_dismiss === true
)

export const getCalendarEntryMeetingStatuses = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.calendar_entry_meeting_statuses
)

export const getMeetingPendingCategoryId = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.meeting_pending_category_id
)

export const getMeetingAcceptedCategoryId = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.meeting_accepted_category_id
)

export const getMeetingDeclinedCategoryId = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.meeting_declined_category_id
)

export const getProfilePageBadges = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.profile_page_badges || {}
)

export const getDealAmountAutoUpdateEnabled = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_amount_auto_update_enabled
)

export const getDealAmountCalculatedFieldId = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.deal_amount_calculated_field_id
)

export const getQuickFillTemplates = createSelector(
  getCurrentAccountSelector,
  currentAccount => currentAccount.quick_fill_templates || []
)
